import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { SettingsTab } from './settingsAgentInstall'

/**
 * Settings -> INTEGRARE -> API Integration, calea noua.
 *
 *   Companies::api_integration    -> GET  /settings/api_integration
 *   Companies::api_key_create     -> POST /settings/api_integration/key      (cu `scopes[]`)
 *   (nou)                         -> POST /settings/api_integration/scopes   drepturile cheii active
 *   Companies::api_doc/{slug}     -> GET  /settings/api_integration/doc/{slug}
 *   Companies::api_doc_group/{g}  -> GET  /settings/api_integration/docs.zip?group=
 *
 * CHEIA NU VINE NICIODATA DINTR-UN GET. `ApiIntegrationKey` are doar prefixul
 * si datele; sirul intreg exista o singura data, in raspunsul lui `createApiKey`
 * - fiindca in baza ramane doar amprenta lui, si nimeni nu-l mai poate scoate
 * de nicaieri.
 */

export type ApiIntegrationKey = {
  /** `odapi-xxxxxx`, atat cat sa fie recunoscuta pe ecran. */
  prefix: string
  createdAt: string
  createdBy: string
  /** Gol = niciodata folosita. */
  lastUsedAt: string
  /**
   * Drepturile cheii, cum le citeste api-ul (`ApiKey::allows()`). GOL = cheia
   * e refuzata pe orice capat care cere un drept: CMDB, ADM, Service Catalog.
   */
  scopes: string[]
  /*
   * Aprobarea pentru drepturile de administrare / ADM, cand firma cere SSO
   * pentru administratori (`KeyApproval` pe server). 0 / gol = neaprobata.
   */
  serviceUserId?: number
  serviceUser?: string
  approvedBy?: string
  approvedAt?: string
  expiresAt?: string
  approvalValid?: boolean
}

export type ApiScope = { scope: string; label: string }

export type ApiScopeGroup = { group: string; scopes: ApiScope[] }

export type ApiDocument = {
  slug: string
  title: string
  group: string
}

export type ApiIntegrationShell = {
  pageTitle: string
  moduleUrl: string
  tabs: SettingsTab[]
  /** `settings.my_company.edit` */
  canWrite: boolean
  /** Gol cand instalarea nu stie adresa api-ului. */
  apiUrl: string
  key: ApiIntegrationKey | null
  docs: ApiDocument[]
  /** Gol cand lista a venit; altfel motivul, cu vorbele serverului. */
  docsError: string
  /** Lista exacta a drepturilor cerute de capetele api-ului, pe familii. */
  scopeCatalog: ApiScopeGroup[]
  /** Firma cere SSO pentru administratori: drepturile din `privilegedScopes` cer aprobare. */
  adminSsoRequired?: boolean
  /**
   * ADMIN_SSO_KEY_ENFORCE pe server. `false` = aprobarea doar se masoara: cheia
   * se salveaza si fara ea, iar ce s-ar fi refuzat se scrie in jurnal.
   */
  adminSsoKeyEnforced?: boolean
  privilegedScopes?: string[]
  /** Cel mai lung termen al unei chei aprobate, in zile. */
  maxKeyDays?: number
  /** Omul de acum poate aproba (intrat prin SSO, nu contul de urgenta). */
  canApprove?: boolean
  /** Conturile locale din care se alege contul de serviciu. */
  serviceAccounts?: Array<{ id: number; name: string }>
}

/** Aprobarea trimisa odata cu drepturile, cand o cer. */
export type KeyApprovalInput = { serviceUserId: number; expiresDays: number }

function approvalFields(approval?: KeyApprovalInput): Record<string, number> {
  return approval ? { serviceUserId: approval.serviceUserId, expiresDays: approval.expiresDays } : {}
}

export const apiIntegrationShellQuery = queryOptions({
  queryKey: ['settings', 'api_integration'],
  queryFn: () => apiGet<ApiIntegrationShell>('/settings/api_integration'),
})

/**
 * O cheie noua; cea de dinainte se opreste in aceeasi trecere.
 *
 * `key` din raspuns e singura data cand cheia se poate citi. NU se pune in
 * queryClient si nu se reincarca ecranul dupa: o reimprospatare ar sterge
 * singurul loc in care exista.
 */
export function createApiKey(scopes: string[] = [], approval?: KeyApprovalInput) {
  return apiPost<Omit<ApiIntegrationShell, 'key'> & { saved: boolean; key: string }>('/settings/api_integration/key', {
    scopes,
    ...approvalFields(approval),
  })
}

/** Drepturile cheii active, schimbate pe loc: cheia ramane aceeasi. */
export function saveApiKeyScopes(scopes: string[], approval?: KeyApprovalInput) {
  return apiPost<ApiIntegrationShell & { saved: boolean }>('/settings/api_integration/scopes', {
    scopes,
    ...approvalFields(approval),
  })
}

/* ------------------------------------------------------------------ *
 * Jurnalul apelurilor API (`ApiLogScreen` pe server):
 *
 *   GET  /settings/api_integration/log            randurile, cu filtre, pe pagini
 *   GET  /settings/api_integration/log.csv        acelasi filtru, ca fisier
 *   POST /settings/api_integration/log/retention  cate zile se pastreaza
 * ------------------------------------------------------------------ */

export type ApiLogRow = {
  id: number
  at: string
  /** 1 = API-ul public (v4), 2 = API-ul de integrare (/api/v4, cheie). */
  app: number
  serviceProvider: number | null
  companyId: number | null
  keyId: number | null
  keyPrefix: string
  userId: number | null
  method: string
  route: string
  status: number
  ms: number
  ip: string
}

export type ApiLogFilters = {
  from?: string
  to?: string
  app?: string
  key?: string
  user?: string
  company?: string
  method?: string
  route?: string
  status?: string
  ip?: string
  page?: number
}

export type ApiLogPage = {
  pageTitle: string
  moduleUrl: string
  tabs: SettingsTab[]
  rows: ApiLogRow[]
  total: number
  page: number
  pageSize: number
  retentionDays: number
  retentionMin: number
  retentionMax: number
  canWrite: boolean
  /** Se vad si randurile fara furnizor (numai administratorul de platforma). */
  unattributed: boolean
}

/** Numai filtrele completate, ca parametri de adresa. */
export function apiLogQuery(filters: ApiLogFilters): string {
  const params = new URLSearchParams()
  for (const [name, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && String(value).trim() !== '') params.set(name, String(value).trim())
  }
  const text = params.toString()

  return text === '' ? '' : `?${text}`
}

export const apiLogQueryOptions = (filters: ApiLogFilters) =>
  queryOptions({
    queryKey: ['settings', 'api_integration', 'log', filters],
    queryFn: () => apiGet<ApiLogPage>(`/settings/api_integration/log${apiLogQuery(filters)}`),
  })

/** Exportul: legatura apasata de om, cu acelasi filtru (fara pagina). */
export function apiLogCsvUrl(filters: ApiLogFilters): string {
  const { page: _page, ...rest } = filters

  return `/api/v2/settings/api_integration/log.csv${apiLogQuery(rest)}`
}

export function saveApiLogRetention(retentionDays: number) {
  return apiPost<{ saved: boolean; retentionDays: number }>('/settings/api_integration/log/retention', { retentionDays })
}

/** Legaturile de descarcare: apasate de om, cu cookie-ul de sesiune pe ele. */
export function apiDocUrl(slug: string): string {
  return `/api/v2/settings/api_integration/doc/${encodeURIComponent(slug)}`
}

export function apiDocGroupUrl(group: string): string {
  return `/api/v2/settings/api_integration/docs.zip?group=${encodeURIComponent(group)}`
}
